<script setup>
import { ref, watch, computed } from 'vue';
import Form from './treeViewSearchForm.vue';
import Historic from "./treeViewSearchHistoric.vue";
import TreeViewSearchItemsList from "./treeViewSearchList.vue";
import { ITEMS_LIST_MODULE_NAME, useStore } from "../plugins/store";
import { TREE_VIEW_NOTIFICATIONS } from "../../treeView/notifications";
import { fetchStructures, NoResultsError } from "../structure-api.helper";

const searchValue = ref('');
const isDropdownOpen = ref(false);
const isMobileStructureActive = ref(false);
const formWidth = ref();
const loading = ref();

const taId = ref(Number(document
    .querySelector("#tree-view-listing-ta .active")
    .getAttribute('data-attr-ta')
));
window.imaios.pubsub.subscribe(window.imaios.pubsub, TREE_VIEW_NOTIFICATIONS.CURRENT_TA_CHANGE, async (changedTaId) => {
  taId.value = Number(changedTaId);

  if (searchValue.value === '') {
    return;
  }
  loading.value = true;
  await sendRequest();
  loading.value = false;
  openDropdown();
});

let debounce;
const onFormInputChange = async () => {
  clearTimeout(debounce);

  if (searchValue.value === '') {
    return;
  }

  loading.value = true;
  debounce = setTimeout(async () => {
    await sendRequest();
    loading.value = false;
  }, 500);
}
const errorResponse = ref(false);
const emptyResponse = ref(false);

const sendRequest = async () => {
  try {
    emptyResponse.value = errorResponse.value = false;
    await fetchStructures(taId.value, searchValue.value);
  } catch (e) {
    if (e instanceof NoResultsError) {
      emptyResponse.value = true;
    } else {
      errorResponse.value = true;
      throw e;
    }
  }
}


const openDropdown = () => isDropdownOpen.value = true;
const closeDropdown = () => isDropdownOpen.value = false;

const onFormFocus = () => {
  openDropdown();
  setMobileFullScreen();
}

const onFormClose = () => {
  searchValue.value = '';
  closeDropdown();
}

window.imaios.pubsub.subscribe(window.imaios.pubsub, TREE_VIEW_NOTIFICATIONS.CLOSE_SVG_MOBILE_SEARCH, () => {
  unsetMobileFullScreen();
  searchValue.value = '';
});

window.imaios.pubsub.subscribe(window.imaios.pubsub, TREE_VIEW_NOTIFICATIONS.DRAGHOLDER_MOUSE_MOVE, () => {
  closeDropdown();
});

window.imaios.pubsub.subscribe(window.imaios.pubsub, TREE_VIEW_NOTIFICATIONS.DRAGHOLDER_MOUSE_UP, () => {
  formWidth.value = document.getElementById('tree-view-search-form').offsetWidth;
});

const unsetMobileFullScreen = () => {
  document.body.classList.remove('not-able-scroll-on-search-full');
  isMobileStructureActive.value = false;
}
const setMobileFullScreen = () => {
  document.body.classList.add('not-able-scroll-on-search-full');
  isMobileStructureActive.value = true;
}

watch(isMobileStructureActive, async (isMobileStructureActiveUpdated) => {
  //once the whole treeview is Vue, the reactive prop will be sufficient: remove this watcher and pub/sub
  window.imaios.pubsub.publish(TREE_VIEW_NOTIFICATIONS.UPDATE_MOBILE_STRUCTURE, { active: isMobileStructureActiveUpdated });
})

const showHistoric = computed(() => searchValue.value.length === 0);
</script>

<template>

  <div v-click-outside="closeDropdown"> <!--todo: use a <template> instead of this div when vue (3) allows it-->
    <Form id="tree-view-search-form"
          v-model="searchValue"
          @input="onFormInputChange"
          @close="onFormClose"
          @focus="onFormFocus"
          :is-mobile-structure-active="isMobileStructureActive"
    />
    <div v-if="isDropdownOpen" :class="{tree_view_search_result__Container: true, 'mobile-structure': isMobileStructureActive}" :style="{'min-width': formWidth+'px'}" >

      <Historic v-if="showHistoric" :current-ta-id="taId"/>
      <div v-else-if="loading" class="item-search structure-waiting">
        <div class="icon-container">
          <div class="icon-search loading"/>
        </div>
        <div class="content-search">
          <div class="search-title-link">
            <div class="search-title">
              <div class=" language-one loading"/>
            </div>
          </div>
          <div class="search-hierarchy">
            <div class="loading"/>
          </div>
        </div>
      </div>
      <template v-else>
        <div v-if="errorResponse" class="item-search structure-waiting">
          <div class="content-search">
            {{ $t("search.tree-view.error") }}
          </div>
        </div>
        <div v-else-if="emptyResponse" class="item-search structure-waiting">
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" fill="none" viewBox="0 0 25 25">
            <path fill="#565B60" fill-rule="evenodd" d="M16.6 17.26a8 8 0 1 1 1.07-1.05l3.61 3.61a.75.75 0 0 1-1.06 1.06l-3.62-3.62Zm1.43-6.2a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z" clip-rule="evenodd"/>
          </svg>
          <div class="content-search">
            {{ $t("search.tree-view.no-result") }}
          </div>
        </div>
        <template v-else>
          <TreeViewSearchItemsList :items="useStore().getters[ITEMS_LIST_MODULE_NAME + '/items']"/>
        </template>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@use '../../../../../assets/css/theme/helpers/variables';
@use '../../../../../assets/css/theme/base/animations';

$mobileHeaderHeight: 110px;

.tree_view_search_result__Container {
  display: flex;
  width: 550px;
  max-height: 350px;
  flex-direction: column;
  margin-top: 10px;
  align-items: flex-start;
  border-radius: 4px;
  background: variables.$dropdown-bg;
  color: variables.$dropdown-color;
  position: absolute;
  z-index: 10;
  overflow: hidden;
  overflow-y: auto;

  /* Light/default */
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.08);

  .language-two {
    color: variables.$body-color;
    font-size: 13px;
  }

  .language-one {
    color: variables.$headings-color;
    font-size: 15px;
  }

  .item-search {
    padding: 8px 20px 6px 15px;
    align-items: center;
    /* gap: 12px; */
    display: grid;
    grid-template-columns: 1fr 18fr;
    grid-gap: 16px;
    /* border: 1px solid black; */
    width: 100%;

    .icon-container {
      .icon-search {
        width: 28px;
        height: 28px;
        margin: auto;

        background-position: 50%;
        background-repeat: no-repeat;
        background-size: cover;
        //background-color: grey;
      }
    }

    .search-title-link {
      text-decoration: none;
    }

    .search-title {
      font-size: 15px;
      font-style: normal;
      line-height: normal;
    }

    .search-hierarchy a {
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px; /* 138.462% */
      text-decoration: none;
      color: variables.$body-color;
    }
  }

  .item-search.structure-waiting {
    display: flex;
    width: 550px;
    max-height: 400px;
    color: variables.$dropdown-color;
    font-size: 15px;
    font-style: italic;
    font-weight: 400;
    line-height: normal;
    display: flex;
    padding: 15px 15px;
    align-items: center;
    gap: 12px;
    align-self: stretch;
  }

  .item-search.active {
    background-color: variables.$dropdown-link-hover-bg;
  }

  .item-search.active .content-search .search-title {
    .language-two, .language-one {
      color: variables.$dropdown-link-hover-color;
    }
  }

  .item-search .content-search .search-hierarchy a{
    //position and z-index to override the stretched-link used on a parent
    position: relative;
    z-index: 2;
  }

  .item-search.active .content-search .search-hierarchy a{
    text-decoration: none;
  }

  .item-search .content-search .search-hierarchy a span:hover{
    background-color: variables.$layer-accent-selected-02 !important;
    text-decoration: underline;
    color: variables.$dropdown-link-hover-color;
  }

  .content-search {
    p {
      margin: 0;
    }
    a {
      text-decoration: underline;
    }
  }

}

.loading {
  @include animations.useShimmerAnimation(1s);
}
.search-title .loading {
  width: 250px;
  height: 15px;
  margin-bottom: 8px;
}
.search-hierarchy .loading {
  width: 200px;
  height: 8px;
}

@media (max-width: 992px) {
  .tree_view_search_result__Container.mobile-structure {
    width: 100%;
    position: relative;
    height: calc(100% - #{$mobileHeaderHeight});
    box-shadow: none;
    max-height: inherit;
    margin-top: 0;

    .item-search {
      grid-template-columns: 1fr 100fr;
    }
    .item-search.structure-waiting {
      display: flex;
      width: 100%;
      max-height: 400px;
      color: variables.$body-color;
      font-size: 15px;
      font-style: italic;
      font-weight: 400;
      line-height: normal;
      display: flex;
      padding: 15px 15px;
      align-items: center;
      gap: 12px;
      align-self: stretch;
      svg {
        width: 35px;
        height: 25px;
      }
    }
  }

  .not-able-scroll-on-search-full {
    overflow:hidden;
  }
}

</style>
